import React, { useCallback, useEffect, useState } from "react";

import {
    fetchContentQuestionNotificationsApi,
    markAllContentQuestionNotificationsReadApi,
    markContentQuestionNotificationReadApi,
} from "../../../../services/moduleContentQuestionNotificationsApi";
import {
    createContentAnswerApi,
    createContentQuestionApi,
    deleteQuestionAttachmentApi,
    downloadQuestionAttachmentApi,
    fetchContentQuestionsApi,
    resolveContentQuestionApi,
    setContentBestAnswerApi,
    toggleAnswerReactionApi,
    toggleQuestionReactionApi,
    uploadAnswerAttachmentApi,
    uploadQuestionAttachmentApi,
} from "../../../../services/moduleContentQuestionsApi";
import ContentQuestionFilters from "./ContentQuestionFilters";
import ContentQuestionForm from "./ContentQuestionForm";
import ContentQuestionNotifications from "./ContentQuestionNotifications";
import ContentQuestionThread from "./ContentQuestionThread";
import "./ContentQuestions.css";

const extractErrorMessage = (error) =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || "Action impossible pour le moment.";

export default function ContentQuestionsPanel({ moduleId, content, context, onNavigateContext }) {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [highlighted, setHighlighted] = useState({ questionId: null, answerId: null });
    const [formOpen, setFormOpen] = useState(false);
    const [filters, setFilters] = useState({
        status: "ALL",
        mine: false,
        tag: "",
        sort: "RECENT",
    });

    const loadQuestions = useCallback(async () => {
        if (!moduleId || !content?.id) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchContentQuestionsApi(moduleId, content.id, filters);
            setQuestions(response.data || []);
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [content?.id, filters, moduleId]);

    useEffect(() => {
        setFormOpen(false);
        loadQuestions();
    }, [loadQuestions]);

    const loadNotifications = useCallback(async () => {
        setNotificationsLoading(true);
        try {
            const response = await fetchContentQuestionNotificationsApi();
            setNotifications(response.data || []);
        } catch {
            setNotifications([]);
        } finally {
            setNotificationsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    const uploadFiles = async (files, uploadFn) => {
        if (!files?.length) return;
        for (let index = 0; index < files.length; index += 1) {
            const file = files[index];
            setUploadStatus(`Upload ${index + 1}/${files.length} - ${file.name}`);
            await uploadFn(file, (event) => {
                if (!event.total) return;
                const percent = Math.round((event.loaded * 100) / event.total);
                setUploadStatus(`Upload ${index + 1}/${files.length} - ${percent}%`);
            });
        }
        setUploadStatus(null);
    };

    const handleCreateQuestion = async (payload) => {
        setSubmitting(true);
        setError(null);
        try {
            const { files, ...questionPayload } = payload || {};
            const response = await createContentQuestionApi(moduleId, content.id, questionPayload);
            const createdQuestion = response.data;
            await uploadFiles(files, (file, onUploadProgress) =>
                uploadQuestionAttachmentApi(moduleId, content.id, createdQuestion.id, file, onUploadProgress)
            );
            setFormOpen(false);
            await loadQuestions();
            await loadNotifications();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
            setUploadStatus(null);
        }
    };

    const handleAnswer = async (questionId, payload) => {
        setSubmitting(true);
        setError(null);
        try {
            const { files, ...answerPayload } = payload || {};
            const response = await createContentAnswerApi(moduleId, content.id, questionId, answerPayload);
            const createdAnswer = response.data;
            await uploadFiles(files, (file, onUploadProgress) =>
                uploadAnswerAttachmentApi(moduleId, content.id, questionId, createdAnswer.id, file, onUploadProgress)
            );
            await loadQuestions();
            await loadNotifications();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
            setUploadStatus(null);
        }
    };

    const handleResolve = async (questionId) => {
        setSubmitting(true);
        setError(null);
        try {
            await resolveContentQuestionApi(moduleId, content.id, questionId);
            await loadQuestions();
            await loadNotifications();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleBestAnswer = async (questionId, answerId) => {
        setSubmitting(true);
        setError(null);
        try {
            await setContentBestAnswerApi(moduleId, content.id, questionId, answerId);
            await loadQuestions();
            await loadNotifications();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const applyOptimisticReaction = (item, reactionType) => {
        const activeKey = reactionType === "LIKE" ? "likedByCurrentUser" : "helpfulByCurrentUser";
        const countKey = reactionType === "LIKE" ? "likeCount" : "helpfulCount";
        const wasActive = item?.[activeKey] === true;
        return {
            ...item,
            [activeKey]: !wasActive,
            [countKey]: Math.max(0, Number(item?.[countKey] || 0) + (wasActive ? -1 : 1)),
        };
    };

    const handleQuestionReaction = async (questionId, reactionType) => {
        setQuestions((current) => current.map((question) =>
            String(question.id) === String(questionId) ? applyOptimisticReaction(question, reactionType) : question
        ));
        setError(null);
        try {
            await toggleQuestionReactionApi(moduleId, content.id, questionId, reactionType);
            await loadQuestions();
        } catch (err) {
            setError(extractErrorMessage(err));
            await loadQuestions();
        }
    };

    const handleAnswerReaction = async (questionId, answerId, reactionType) => {
        setQuestions((current) => current.map((question) => {
            if (String(question.id) !== String(questionId)) return question;
            return {
                ...question,
                answers: (question.answers || []).map((answer) =>
                    String(answer.id) === String(answerId) ? applyOptimisticReaction(answer, reactionType) : answer
                ),
            };
        }));
        setError(null);
        try {
            await toggleAnswerReactionApi(moduleId, content.id, questionId, answerId, reactionType);
            await loadQuestions();
        } catch (err) {
            setError(extractErrorMessage(err));
            await loadQuestions();
        }
    };

    const handleDownloadAttachment = async (attachment) => {
        setError(null);
        try {
            const response = await downloadQuestionAttachmentApi(moduleId, content.id, attachment.id);
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: attachment.mimeType || "application/octet-stream" }));
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = attachment.originalFileName || "piece-jointe";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            setError(extractErrorMessage(err));
        }
    };

    const handleDeleteAttachment = async (attachment) => {
        setSubmitting(true);
        setError(null);
        try {
            await deleteQuestionAttachmentApi(moduleId, content.id, attachment.id);
            await loadQuestions();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const scrollToNotificationTarget = async (notification) => {
        if (!notification) return;
        await markContentQuestionNotificationReadApi(notification.id);
        await loadNotifications();
        if (String(notification.contentId) !== String(content.id)) {
            setError("Cette notification concerne un autre contenu du module.");
            return;
        }
        setHighlighted({ questionId: notification.questionId, answerId: notification.answerId });
        requestAnimationFrame(() => {
            const targetId = notification.answerId ? `cq-answer-${notification.answerId}` : `cq-question-${notification.questionId}`;
            document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        window.setTimeout(() => setHighlighted({ questionId: null, answerId: null }), 2500);
    };

    const handleMarkNotificationRead = async (notificationId) => {
        setNotificationsLoading(true);
        try {
            await markContentQuestionNotificationReadApi(notificationId);
            await loadNotifications();
        } finally {
            setNotificationsLoading(false);
        }
    };

    const handleMarkAllNotificationsRead = async () => {
        setNotificationsLoading(true);
        try {
            await markAllContentQuestionNotificationsReadApi();
            await loadNotifications();
        } finally {
            setNotificationsLoading(false);
        }
    };

    return (
        <div className="cq-panel">
            <div className="cq-toolbar">
                <div>
                    <h3>Questions / Reponses</h3>
                    <p>{content?.title || "Contenu courant"}</p>
                </div>
                <button type="button" className="cq-btn cq-btn--primary" onClick={() => setFormOpen(true)}>
                    Poser une question
                </button>
            </div>

            {formOpen ? (
                <ContentQuestionForm
                    moduleId={moduleId}
                    context={context}
                    onCancel={() => setFormOpen(false)}
                    onSubmit={handleCreateQuestion}
                    submitting={submitting}
                />
            ) : null}

            <ContentQuestionFilters filters={filters} onChange={setFilters} />

            <ContentQuestionNotifications
                notifications={notifications}
                loading={notificationsLoading}
                onOpen={scrollToNotificationTarget}
                onMarkRead={handleMarkNotificationRead}
                onMarkAllRead={handleMarkAllNotificationsRead}
            />

            {error ? <div className="cq-state cq-state--error">{error}</div> : null}
            {uploadStatus ? <div className="cq-state">{uploadStatus}</div> : null}
            {loading ? <div className="cq-state">Chargement des questions...</div> : null}

            {!loading && questions.length === 0 ? (
                <div className="cq-empty">Aucune question pour ce contenu.</div>
            ) : null}

            <div className="cq-list">
                {questions.map((question) => (
                    <ContentQuestionThread
                        key={question.id}
                        question={question}
                        onAnswer={handleAnswer}
                        onResolve={handleResolve}
                        onBestAnswer={handleBestAnswer}
                        onQuestionReaction={handleQuestionReaction}
                        onAnswerReaction={handleAnswerReaction}
                        onDownloadAttachment={handleDownloadAttachment}
                        onDeleteAttachment={handleDeleteAttachment}
                        onNavigateContext={onNavigateContext}
                        submitting={submitting}
                        moduleId={moduleId}
                        highlightedQuestionId={highlighted.questionId}
                        highlightedAnswerId={highlighted.answerId}
                    />
                ))}
            </div>
        </div>
    );
}
